/* eslint-disable no-async-promise-executor */
import type { Token } from '@/types/models';
import type { JwtPayload } from 'jwt-decode';
import { jwtDecode } from 'jwt-decode';

// Co-located from types/common (single consumer, #807 Phase A).
export type JwtStatus = 'VALID' | 'EXPIRED' | 'NO_EXPIRATION_TIME' | 'INVALID';

/**
 * Parse the `/auth/refresh-token` response body into a `Token`, tolerant of the
 * different shapes the backend envelope may produce. Single source of truth shared
 * by the axios response interceptor and the authen store so they never diverge.
 *   - flat:           { accessToken, refreshToken }                 (swagger RefreshTokenResponse)
 *   - token-wrapped:  { token: { accessToken, refreshToken } }
 *   - data-enveloped: { data: { token: { accessToken, refreshToken } } }
 *   - data-flat:      { data: { accessToken, refreshToken } }
 * Returns `null` when accessToken/refreshToken cannot be located.
 */
export function parseRefreshResponse(body: unknown): Token | null {
  const b = body as Record<string, any> | null | undefined;
  const candidate =
    b?.token ??
    b?.data?.token ??
    (b?.accessToken ? b : undefined) ??
    (b?.data?.accessToken ? b.data : undefined);

  if (candidate?.accessToken && candidate?.refreshToken) {
    return {
      accessToken: candidate.accessToken,
      refreshToken: candidate.refreshToken,
    };
  }
  return null;
}
export const decodeJWT = (token: string): Promise<JwtPayload | null> => {
  return new Promise((resolve) => {
    if (token) {
      const decodedToken = jwtDecode<JwtPayload>(token);
      resolve(decodedToken);
    } else {
      resolve(null);
    }
  });
};
export const canRefreshToken = async (
  token: string,
  showLog = false,
  from: string | undefined = undefined,
): Promise<boolean> => {
  return new Promise(async (resolve) => {
    const decodeToken = await decodeJWT(token);
    if (decodeToken && decodeToken.exp) {
      const expirationTime = decodeToken.exp * 1000; // Convert to milliseconds
      const currentTime = new Date().getTime();
      const timeUntilExpiration = expirationTime - currentTime;
      const refreshThreshold = 5 * (60 * 1000); // 5 min
      const refeshIt = timeUntilExpiration < refreshThreshold;
      if (showLog) {
        console.log(
          'jwtUtil > canRefreshToken > refeshIt : ' + refeshIt,
          decodeToken,
          'from',
          from,
        );
      }

      resolve(refeshIt);
      return;
    }

    resolve(false);
  });
};
export const getRefreshTokenTimeout = async (
  token: string,
): Promise<number> => {
  return new Promise(async (resolve) => {
    const decodeToken = await decodeJWT(token);
    if (decodeToken && decodeToken.exp) {
      const expires = new Date(decodeToken.exp * 1000);
      const timeout = expires.getTime() - Date.now() - 5 * (60 * 1000);
      resolve(timeout);
    }
    resolve(0);
  });
};
export const getTokenStatus = async (token: string): Promise<JwtStatus> => {
  return new Promise(async (resolve) => {
    try {
      const decodeToken = await decodeJWT(token);
      if (decodeToken && decodeToken.exp) {
        // Get the current time in seconds since the epoch
        // const currentTime = Math.floor(Date.now() / 1000);
        const currentTime = new Date().getTime() + 3000; // add 3 more seconds in case not expired when fetch new data from server
        const expirationTime = decodeToken.exp * 1000; // Convert to milliseconds
        // Check if the token is expired
        if (decodeToken && currentTime > expirationTime) {
          // if (decodeToken && currentTime > decodeToken.exp) {
          resolve('EXPIRED');
        } else {
          resolve('VALID');
        }
      } else {
        resolve('NO_EXPIRATION_TIME');
      }
    } catch (err) {
      resolve('INVALID');
    }
  });
};

/**
 * Route guard: session is OK if both tokens exist and the refresh token JWT is still VALID.
 * Access token may be EXPIRED — axios interceptor will renew it.
 */
export async function isSessionPresentAndRefreshable(
  accessToken: string,
  refreshToken: string,
): Promise<boolean> {
  if (!accessToken?.trim() || !refreshToken?.trim()) {
    return false;
  }
  const refreshTokenStatus = await getTokenStatus(refreshToken);
  return refreshTokenStatus === 'VALID';
}